import { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
    Sheet,
    SheetContent,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    Loader2,
    X,
    Check,
    Receipt,
    Wallet,
    Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { DEPOSIT_LEDGER_KEY } from '@/components/insurance-sub/depositLedger';
import { PREMIUM_YEARS_KEY } from '@/components/insurance-sub/premiumLedger';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import TransactionSelector from '@/components/ui/TransactionSelector';

interface RecordInsuranceRepaymentSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    insuranceLoanId: number;
    policyName?: string;
    repaymentId?: number | null; // For edit mode
    transactionId?: number | null; // Pre-selected transaction ID for edit mode
    isEditingDeposit?: boolean; // true if editing a deposit, false if editing a repayment
    onSuccess?: () => void;
}

export default function RecordInsuranceRepaymentSheet({
    open,
    onOpenChange,
    insuranceLoanId,
    policyName,
    repaymentId,
    transactionId: initialTransactionId,
    isEditingDeposit = false,
    onSuccess,
}: RecordInsuranceRepaymentSheetProps) {
    const queryClient = useQueryClient();
    const isEditMode = !!repaymentId;
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [selectedTransactionId, setSelectedTransactionId] = useState<number | null>(null);
    const [isDeposit, setIsDeposit] = useState(false);
    // Populate form when editing - use passed transactionId directly instead of querying
    useEffect(() => {
        if (isEditMode && initialTransactionId && open) {
            setSelectedTransactionId(initialTransactionId);
            setIsDeposit(isEditingDeposit);
        } else if (!repaymentId && open) {
            resetForm();
        }
    }, [initialTransactionId, repaymentId, open, isEditMode, isEditingDeposit]);

    const resetForm = () => {
        setSelectedTransactionId(null);
        setIsDeposit(false);
    };

    const isDirty = selectedTransactionId !== null;

    const handleClose = () => {
        if (isDirty && !isEditMode) { // Simple dirty check, more permissive in edit mode
            setShowCloseConfirm(true);
        } else {
            onOpenChange(false);
        }
    };

    const saveMutation = useMutation({
        mutationFn: async () => {
            if (!selectedTransactionId) {
                throw new Error('Please select a transaction');
            }

            if (isEditMode) {
                // Check if payment type changed
                const typeChanged = isDeposit !== isEditingDeposit;

                if (typeChanged) {
                    // Delete from old table
                    const oldTable = isEditingDeposit ? 'insurance_loan_deposits' : 'insurance_loan_repayments';
                    const { error: deleteError } = await supabase
                        .from(oldTable)
                        .delete()
                        .eq('id', repaymentId);
                    if (deleteError) throw deleteError;

                    // Insert into new table
                    const newTable = isDeposit ? 'insurance_loan_deposits' : 'insurance_loan_repayments';
                    const { error: insertError } = await supabase
                        .from(newTable)
                        .insert({
                            loan_id: insuranceLoanId,
                            transaction_id: selectedTransactionId,
                        });
                    if (insertError) throw insertError;
                } else {
                    // Same type, just update transaction_id
                    const tableName = isDeposit ? 'insurance_loan_deposits' : 'insurance_loan_repayments';
                    const { error: updateError } = await supabase
                        .from(tableName)
                        .update({ transaction_id: selectedTransactionId })
                        .eq('id', repaymentId);
                    if (updateError) throw updateError;
                }

                return { id: repaymentId };
            } else {
                // INSERT: Create new record in appropriate table
                const tableName = isDeposit ? 'insurance_loan_deposits' : 'insurance_loan_repayments';
                const { error: insertError } = await supabase
                    .from(tableName)
                    .insert({
                        loan_id: insuranceLoanId,
                        transaction_id: selectedTransactionId,
                    });
                if (insertError) throw insertError;

                return { id: selectedTransactionId };
            }
        },
        onSuccess: () => {
            const successMessage = isEditMode
                ? 'Repayment updated successfully'
                : isDeposit
                    ? 'Deposit recorded successfully'
                    : 'Repayment recorded successfully';
            toast.success(successMessage);
            queryClient.invalidateQueries({ queryKey: ['contract-insights'] });
            queryClient.invalidateQueries({ queryKey: ['insurances'] });
            queryClient.invalidateQueries({ queryKey: ['insurance-repayment-detail'] });
            queryClient.invalidateQueries({ queryKey: ['unassigned-transactions'] });
            // THE TWO ESTATE PAGES READ WHAT THIS SHEET JUST WROTE. Both are whole-table reads on
            // one cache entry each, and neither was invalidated by any writer: a deposit recorded
            // here left the Deposits ledger and the Premium Years table — whose instrument and
            // "Paid by driver" column are functions of exactly this row — stale until a hard
            // refetch. Both keys are invalidated whichever side of the toggle was written, because
            // an edit can move a row from one table to the other.
            queryClient.invalidateQueries({ queryKey: DEPOSIT_LEDGER_KEY });
            queryClient.invalidateQueries({ queryKey: PREMIUM_YEARS_KEY });
            resetForm();
            onOpenChange(false);
            onSuccess?.();
            setShowCloseConfirm(false);
        },
        onError: (error) => {
            toast.error(`Failed to ${isEditMode ? 'update' : 'record'} repayment: ${error.message}`);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (!repaymentId || !initialTransactionId) {
                throw new Error('No repayment to delete');
            }

            // Delete the deposit/repayment record first (FK constraint)
            const tableName = isEditingDeposit ? 'insurance_loan_deposits' : 'insurance_loan_repayments';
            const { error: deleteRepaymentError } = await supabase
                .from(tableName)
                .delete()
                .eq('id', repaymentId);
            if (deleteRepaymentError) throw deleteRepaymentError;

            // Delete the linked transaction
            const { error: deleteTransactionError } = await supabase
                .from('transactions')
                .delete()
                .eq('id', initialTransactionId);
            if (deleteTransactionError) throw deleteTransactionError;
        },
        onSuccess: () => {
            toast.success('Repayment and linked transaction deleted');
            queryClient.invalidateQueries({ queryKey: ['contract-insights'] });
            queryClient.invalidateQueries({ queryKey: ['insurances'] });
            queryClient.invalidateQueries({ queryKey: ['insurance-repayment-detail'] });
            queryClient.invalidateQueries({ queryKey: ['unassigned-transactions'] });
            // A deleted deposit moves the same two pages the insert did — including the loan's
            // instrument, which a removed payment can flip back from "Paid in full" to an advance.
            queryClient.invalidateQueries({ queryKey: DEPOSIT_LEDGER_KEY });
            queryClient.invalidateQueries({ queryKey: PREMIUM_YEARS_KEY });
            setShowDeleteConfirm(false);
            resetForm();
            onOpenChange(false);
            onSuccess?.();
        },
        onError: (error) => {
            toast.error(`Failed to delete repayment: ${error.message}`);
        },
    });

    const handleSubmit = () => {
        if (!selectedTransactionId) {
            toast.error('Please select a transaction');
            return;
        }
        saveMutation.mutate();
    };

    return (
        <>
            <Sheet open={open} onOpenChange={handleClose}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 !h-auto !p-0 !w-[85vw] !max-w-4xl rounded-2xl border shadow-2xl bg-background overflow-hidden flex gap-0"
                >
                    {/* Left Sidebar - Progress/Context */}
                    <div className="w-1/3 bg-muted/30 border-r border-border/50 p-6 hidden md:flex flex-col relative overflow-hidden">
                        {/* Decorative background elements */}
                        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
                        <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

                        <div className="mb-8 relative z-10">
                            <h2 className="text-2xl font-semibold tracking-tight">
                                {isEditMode ? 'Edit Repayment' : 'Add Repayment'}
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                {isEditMode ? 'Update the repayment details below.' : 'Record a new payment for this insurance policy.'}
                            </p>
                            {policyName && (
                                <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                                    <Receipt className="h-3 w-3" />
                                    {policyName}
                                </div>
                            )}
                        </div>

                        {/* Steps / Context Indicators */}
                        <div className="space-y-1 relative z-10 flex-1">
                            <div className="group flex items-center gap-3 p-3 rounded-xl bg-background shadow-sm ring-1 ring-border transition-all duration-300">
                                <div className="h-10 w-10 rounded-full flex items-center justify-center bg-primary text-primary-foreground transition-colors duration-300">
                                    <Receipt className="h-5 w-5" />
                                </div>
                                <div>
                                    <p className="font-medium text-sm text-foreground">
                                        Repayment Details
                                    </p>
                                    <p className="text-xs text-muted-foreground/70 hidden lg:block">
                                        Select Transaction
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Sidebar bottom action */}
                        {isEditMode ? (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="mt-auto w-fit text-destructive hover:text-destructive p-0 h-auto hover:bg-transparent"
                                onClick={() => setShowDeleteConfirm(true)}
                                disabled={deleteMutation.isPending}
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Repayment
                            </Button>
                        ) : (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="mt-auto w-fit text-muted-foreground hover:text-foreground p-0 h-auto hover:bg-transparent"
                                onClick={() => onOpenChange(false)}
                            >
                                <X className="h-4 w-4 mr-2" />
                                Cancel & Close
                            </Button>
                        )}
                    </div>

                    {/* Right Content */}
                    <div className="flex-1 flex flex-col h-full relative">
                        {/* Mobile Header (visible only on small screens) */}
                        <div className="md:hidden p-4 border-b flex items-center justify-between">
                            <div>
                                <h2 className="font-semibold">{isEditMode ? 'Edit Repayment' : 'Add Repayment'}</h2>
                                {policyName && <p className="text-xs text-muted-foreground">{policyName}</p>}
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)}>
                                <X className="h-5 w-5" />
                            </Button>
                        </div>

                        {/* Main Form Area */}
                        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 md:p-10">
                            <div className="h-full max-w-lg mx-auto">
                                <div className="mb-8">
                                    <h3 className="text-2xl font-bold tracking-tight">Repayment Details</h3>
                                    <p className="text-muted-foreground">Select a transaction to record as a repayment.</p>
                                </div>

                                <div className="space-y-6">
                                    {/* Payment Type Toggle - shown for both add and edit */}
                                    <div className="space-y-3">
                                        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Payment Type</Label>
                                        <div className="grid grid-cols-2 gap-3">
                                            <button
                                                type="button"
                                                onClick={() => setIsDeposit(false)}
                                                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${!isDeposit
                                                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30'
                                                    : 'border-border hover:border-muted-foreground/50'
                                                    }`}
                                            >
                                                <Receipt className={`h-5 w-5 ${!isDeposit ? 'text-emerald-600' : 'text-muted-foreground'
                                                    }`} />
                                                <div className="text-left">
                                                    <p className={`font-medium text-sm ${!isDeposit ? 'text-emerald-700 dark:text-emerald-400' : 'text-foreground'
                                                        }`}>
                                                        Repayment
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">
                                                        Regular payment
                                                    </p>
                                                </div>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setIsDeposit(true)}
                                                className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${isDeposit
                                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                                                    : 'border-border hover:border-muted-foreground/50'
                                                    }`}
                                            >
                                                <Wallet className={`h-5 w-5 ${isDeposit ? 'text-blue-600' : 'text-muted-foreground'
                                                    }`} />
                                                <div className="text-left">
                                                    <p className={`font-medium text-sm ${isDeposit ? 'text-blue-700 dark:text-blue-400' : 'text-foreground'
                                                        }`}>
                                                        Deposit
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">
                                                        Upfront payment
                                                    </p>
                                                </div>
                                            </button>
                                        </div>
                                        {/* THE LEDGER DOES THE OPPOSITE OF WHAT THIS USED TO SAY.
                                            `total_value` adds `SUM(il.amount)` GROSS and nets the
                                            deposit out of the INTEREST base alone
                                            (`20260823010000…sql:940-951`), so a deposit never
                                            touches the principal — it removes the charge for credit
                                            on the part it covers. Where it covers the whole premium
                                            the interest is zero and the year is papered as an
                                            Insurance Premium Payment Record rather than an advance
                                            [D-B45]. */}
                                        {isDeposit && (
                                            <p className="text-xs text-blue-600 dark:text-blue-400">
                                                A deposit reduces the interest base, not the principal.
                                                A deposit that covers the whole premium leaves nothing
                                                owed and no interest at all.
                                            </p>
                                        )}
                                    </div>

                                    <div className="space-y-3">
                                        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Select Transaction *</Label>
                                        <TransactionSelector
                                            value={selectedTransactionId}
                                            onChange={(id) => setSelectedTransactionId(id)}
                                            placeholder="Select a transaction..."
                                            defaultType="CREDIT"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Sticky Footer */}
                        <div className="p-6 border-t bg-background/80 backdrop-blur-md sticky bottom-0 z-20">
                            <div className="max-w-lg mx-auto flex items-center justify-end gap-3">
                                {isEditMode ? (
                                    <Button
                                        onClick={() => setShowDeleteConfirm(true)}
                                        disabled={deleteMutation.isPending}
                                        className="w-48 rounded-full shadow-lg hover:shadow-red-500/25 transition-all bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                                    >
                                        {deleteMutation.isPending ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Trash2 className="h-4 w-4 mr-2" />
                                        )}
                                        Delete Repayment
                                    </Button>
                                ) : (
                                    <>
                                        <Button
                                            variant="outline"
                                            onClick={() => onOpenChange(false)}
                                            disabled={saveMutation.isPending}
                                            className="w-24 rounded-full"
                                        >
                                            Cancel
                                        </Button>

                                        <Button
                                            onClick={handleSubmit}
                                            disabled={saveMutation.isPending}
                                            className="w-40 rounded-full shadow-lg hover:shadow-emerald-500/25 transition-all bg-emerald-600 hover:bg-emerald-700 text-white"
                                        >
                                            {saveMutation.isPending ? (
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <Check className="h-4 w-4 mr-2" />
                                            )}
                                            Complete
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard changes?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to discard them?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Continue Editing</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onOpenChange(false)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Discard
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this repayment?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete the {isEditingDeposit ? 'deposit' : 'repayment'} record and its <strong>linked bank transaction</strong>. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => deleteMutation.mutate()}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleteMutation.isPending}
                        >
                            {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Delete Everything
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
